/**
 * useFinanceiroDescricaoTemplate
 * -----------------------------------------------------------------------------
 * Hooks do módulo Financeiro para a "Descrição dinâmica com variáveis".
 *
 * Arquitetura (write-time rendering):
 *   - O usuário edita um TEMPLATE (ex.: "Mensalidade {{mes_referencia}} - {{veiculos.placas}}")
 *   - O template é persistido em `descricao_template` (cobrança/assinatura)
 *   - O banco renderiza um SNAPSHOT em `descricao` no momento da criação/emissão
 *   - Leituras (checkout, listagens) continuam lendo `descricao` — custo ZERO
 *
 * RELEASE: 2026-08-19 v1.0.0 - Versão inicial (catálogo, preview e persistência)
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('FinanceiroDescricaoTemplate');

/** Uma variável disponível no catálogo do Financeiro */
export interface FinanceiroVariable {
  /** Token completo, ex.: {{cliente.nome}} */
  token: string;
  /** Categoria de agrupamento (empresa, cliente, gerais, recorrencia, cobranca, veiculos) */
  categoria: string;
  /** Rótulo curto exibido na lista */
  label: string;
  /** Descrição auxiliar */
  descricao: string | null;
  /** Exemplo renderizado com dados reais do tenant */
  exemplo: string | null;
}

/** Rótulos amigáveis por categoria (ordem de exibição) */
export const FINANCEIRO_VARIABLE_CATEGORIES: Array<{ key: string; label: string }> = [
  { key: 'empresa', label: 'Empresa' },
  { key: 'cliente', label: 'Cliente' },
  { key: 'gerais', label: 'Gerais' },
  { key: 'recorrencia', label: 'Recorrência' },
  { key: 'cobranca', label: 'Cobrança' },
  { key: 'veiculos', label: 'Veículos' },
];

/**
 * Catálogo de variáveis disponíveis (RPC `get_financeiro_variables_catalog`).
 * Cacheado por 10 minutos — o conteúdo muda muito raramente.
 */
export function useFinanceiroVariablesCatalog(enabled = true) {
  return useQuery({
    queryKey: ['financeiro-variables-catalog'],
    enabled,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<FinanceiroVariable[]> => {
      const { data, error } = await (supabase.rpc as any)('get_financeiro_variables_catalog');
      if (error) {
        logger.error('Falha ao carregar catálogo de variáveis', { error: error.message });
        throw new Error(error.message);
      }
      return (data ?? []) as FinanceiroVariable[];
    },
  });
}

/** Parâmetros do preview de descrição */
export interface PreviewDescricaoParams {
  template: string;
  cobrancaId?: string | null;
  assinaturaId?: string | null;
  customerId?: string | null;
  vehicleIds?: string[] | null;
}

/**
 * Preview server-side do template (RPC `preview_financeiro_descricao`).
 * Usa exatamente o mesmo renderizador aplicado na emissão — garante fidelidade.
 */
export function usePreviewFinanceiroDescricao() {
  return useMutation({
    mutationFn: async (params: PreviewDescricaoParams): Promise<string> => {
      const { data, error } = await (supabase.rpc as any)('preview_financeiro_descricao', {
        p_template: params.template,
        p_cobranca_id: params.cobrancaId ?? null,
        p_assinatura_id: params.assinaturaId ?? null,
        p_customer_id: params.customerId ?? null,
        p_vehicle_ids: params.vehicleIds?.length ? params.vehicleIds : null,
      });
      if (error) throw new Error(error.message);
      return (data as string) ?? '';
    },
  });
}

/**
 * Persiste o template em uma cobrança ou assinatura já criada
 * (RPC `set_financeiro_descricao_template`), re-renderizando o snapshot.
 *
 * Uso típico: logo após criar o documento financeiro, quando o usuário
 * informou uma descrição contendo variáveis.
 */
export async function persistDescricaoTemplate(args: {
  template: string;
  cobrancaId?: string | null;
  assinaturaId?: string | null;
  tenantHint?: string | null;
}): Promise<void> {
  const { error } = await (supabase.rpc as any)('set_financeiro_descricao_template', {
    p_template: args.template,
    p_cobranca_id: args.cobrancaId ?? null,
    p_assinatura_id: args.assinaturaId ?? null,
    p_tenant_hint: args.tenantHint ?? null,
  });
  if (error) {
    // Falha aqui não pode derrubar o fluxo de criação: o documento já existe
    // com a descrição literal. Logamos para diagnóstico.
    logger.warn('Falha ao persistir template de descrição', { error: error.message });
  }
}

/** Detecta se um texto contém tokens de variável ({{...}}) */
export function hasDescricaoVariables(text?: string | null): boolean {
  return !!text && /\{\{\s*[\w.]+\s*\}\}/.test(text);
}
